import { forwardRef } from "react"
import { FormikProps } from "formik"
import { makeStyles } from "tss-react/mui"
import { useNavigate } from "react-router-dom"

import { MERCHANT_ADMIN_PRICE_LISTS_ROUTE } from "../../../../../../constants/routes"
import { ErrorDisplay, NoDataError, SuccessDisplay } from "../../../../../../core/api/errorTypes"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { StandardAlertInfo } from "../../../../../../shared/components/alert/StandardAlertInfo"
import { toDineroLossy, toMonetaryAmount } from "../../../../../currency/currencyLib"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useDateTime } from "../../../../../localization/useDateTime"

import {
    adjustPriceListPrices,
    fetchDetailedPriceLists,
    FetchDetailedPriceListsResponse,
    PriceListQuery,
} from "../../priceListApi"
import { AdjustmentUnit } from "../../priceListTypes"
import { AdjustPricesForm } from "./AdjustPricesForm"
import { AdjustPricesFormValues, getPriceListCurrency, toPercentageAmount } from "./adjustPricesLib"

const useStyles = makeStyles()(() => ({
    banner: {
        marginBottom: "1.5rem",
    },
}))

const selectAdjustablePriceList = ({ priceList }: FetchDetailedPriceListsResponse) => {
    const currency = getPriceListCurrency(priceList)
    if (!currency) throw new NoDataError()

    return { priceList, currency }
}

type AdjustPricesFormControllerProps = Readonly<{
    priceListId: number
    onSubmitBlockedChange: (isBlocked: boolean) => void
}>

export const AdjustPricesFormController = forwardRef<
    FormikProps<AdjustPricesFormValues>,
    AdjustPricesFormControllerProps
>(function AdjustPricesFormController({ priceListId, onSubmitBlockedChange }, ref) {
    const { classes } = useStyles()
    const navigate = useNavigate()
    const dateTimeFactory = useDateTime()

    const { data } = useApiQuery(fetchDetailedPriceLists, {
        queryName: PriceListQuery.DETAILS,
        dependencies: { id: priceListId },
        select: selectAdjustablePriceList,
        errorDisplay: ErrorDisplay.NONE,
        suspense: true,
        useErrorBoundary: true,
    })

    const { mutateAsync: callAdjustPriceListPrices } = useApiMutation(adjustPriceListPrices, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(_, __, context) {
            await context.queryClient.invalidateQueries({ queryKey: [PriceListQuery.LIST] })
            navigate(MERCHANT_ADMIN_PRICE_LISTS_ROUTE)
        },
    })

    if (!data) return null

    const { priceList, currency } = data

    const handleSubmit = async ({
        adjustmentType,
        unit,
        value,
        rounding,
        effectiveFrom,
        updateExistingOrders,
        productGroupIds,
    }: AdjustPricesFormValues) => {
        if (!effectiveFrom) return

        const amount =
            unit === AdjustmentUnit.AMOUNT
                ? toMonetaryAmount(toDineroLossy(Number(value), currency))
                : toPercentageAmount(Number(value))

        await callAdjustPriceListPrices({
            priceListId,
            adjustment: {
                type: adjustmentType,
                unit,
                value: amount,
                rounding,
            },
            effectiveFrom: dateTimeFactory.toApi(effectiveFrom.startOf("day")),
            updateExistingOrders,
            productGroupIds,
        })
    }

    return (
        <>
            <LocalizedStrict id="adjust-price-list-banner" attrs={{ title: true, description: true }}>
                <StandardAlertInfo
                    severity="warning"
                    fullWidth
                    className={classes.banner}
                    title="A price adjustment creates a new price list"
                    description="The current list is left untouched. You decide when the new list takes effect and which list it takes over from. The old one is deactivated automatically on that date."
                />
            </LocalizedStrict>
            <AdjustPricesForm
                ref={ref}
                priceList={priceList}
                currency={currency}
                onSubmit={handleSubmit}
                onSubmitBlockedChange={onSubmitBlockedChange}
            />
        </>
    )
})
